import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
import {
  condenseOfficeHours,
  type OfficeHourEntry,
} from './PropertyInfoPanel';

// ============================================================================
// Local helpers
// ============================================================================

/**
 * Remove a trailing country segment (USA / U.S.A. / United States [of America])
 * plus any leading comma/whitespace. Keeps the visible address focused on the
 * street/city/state on the marketing page.
 */
function stripCountry(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/\s*,?\s*(u\.?\s*s\.?\s*a\.?|united\s+states(\s+of\s+america)?)\s*$/i, '')
    .trim();
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

function normalizeDayKey(day: string | null | undefined): string {
  return (day ?? '').trim().toLowerCase().slice(0, 3);
}

/**
 * Given the raw office_hours collection, return an entry for every day of the
 * week — filling any missing / partially-blank days with a "closed" placeholder
 * so `condenseOfficeHours` renders them as "Closed" rows.
 */
function padWithClosedDays(hours: OfficeHourEntry[]): OfficeHourEntry[] {
  const byKey = new Map<string, OfficeHourEntry>();
  for (const entry of hours) {
    const key = normalizeDayKey(entry.day);
    if (key && DAY_ORDER.includes(key as (typeof DAY_ORDER)[number]) && !byKey.has(key)) {
      byKey.set(key, entry);
    }
  }
  return DAY_ORDER.map((key) => {
    const existing = byKey.get(key);
    return {
      day: DAY_LABELS[key],
      open_time: existing?.open_time ?? null,
      close_time: existing?.close_time ?? null,
    };
  });
}

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_place_url',
      suggestedFields: ['property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const title = useContentToken('scheduletourform.title');
  const addressLabel = useContentToken('scheduletourform.address_label');
  const hoursLabel = useContentToken('scheduletourform.hours_label');

  const addressLink = useSlotGroup('scheduletourform.address', addressLinkDef);
  const facebook = useSlotGroup('scheduletourform.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('scheduletourform.instagram_link', instagramLinkDef);

  const propertyAddressUrl = usePropertyAddressUrl();
  const propertyAddress = usePropertyField('property_full_address') as
    | string
    | null
    | undefined;

  const officeHours = useCollectionBinding<OfficeHourEntry>(
    'scheduletourform.office_hours',
    { collection: 'office_hours' },
  );

  const formSlot = useBlockSlot('scheduletourform.form_block', {
    defaultValue: 'multihub-tour-scheduler',
  });

  const addressText = stripCountry(addressLink.text || propertyAddress || '');
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const hoursRows =
    officeHours.length > 0 ? condenseOfficeHours(padWithClosedDays(officeHours)) : [];
  const hasSocial = Boolean(facebook.url || instagram.url);

  return (
    <section className="stf-section">
      <div className="stf-container">
        <div className="stf-grid">
          {/* Left column: Address, Hours, Socials */}
          <aside className="stf-info-col">
            <EditableToken
              path="scheduletourform.address_label"
              as="h3"
              className="stf-info-heading"
            >
              {addressLabel || 'Address'}
            </EditableToken>
            <EditableSlotGroup
              groupPath="scheduletourform.address"
              definition={addressLinkDef}
            >
              {addressLink.url || propertyAddressUrl ? (
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="stf-info-text stf-info-link"
                >
                  {addressText}
                </a>
              ) : (
                <span className="stf-info-text">{addressText}</span>
              )}
            </EditableSlotGroup>

            {hoursRows.length > 0 && (
              <>
                <EditableToken
                  path="scheduletourform.hours_label"
                  as="h3"
                  className="stf-info-heading stf-info-heading--spaced"
                >
                  {hoursLabel || 'Hours'}
                </EditableToken>
                <div className="stf-hours">
                  {hoursRows.map((row, i) => (
                    <div key={i} className="stf-hours-row">
                      <span className="stf-hours-day">{row.dayLabel}</span>
                      <span className="stf-hours-time">{row.timeLabel}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className={`stf-socials${hasSocial ? '' : ' stf-socials--empty'}`}>
              <EditableSlotGroup
                groupPath="scheduletourform.facebook_link"
                definition={facebookLinkDef}
              >
                {facebook.url ? (
                  <a
                    href={facebook.url}
                    target={facebook._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={facebook.label || 'Facebook'}
                    className="stf-social-link"
                  >
                    <FontAwesomeIcon icon={faFacebookF} />
                  </a>
                ) : (
                  <span className="stf-social-link stf-social-link--placeholder" aria-hidden="true">
                    <FontAwesomeIcon icon={faFacebookF} />
                  </span>
                )}
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="scheduletourform.instagram_link"
                definition={instagramLinkDef}
              >
                {instagram.url ? (
                  <a
                    href={instagram.url}
                    target={instagram._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={instagram.label || 'Instagram'}
                    className="stf-social-link"
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                ) : (
                  <span className="stf-social-link stf-social-link--placeholder" aria-hidden="true">
                    <FontAwesomeIcon icon={faInstagram} />
                  </span>
                )}
              </EditableSlotGroup>
            </div>
          </aside>

          {/* Right column: title + form */}
          <div className="stf-form-col">
            <EditableToken
              path="scheduletourform.title"
              as="h2"
              className="stf-title"
            >
              {title || 'Schedule A Tour'}
            </EditableToken>

            <EditableToken
              path="scheduletourform.form_block"
              as="div"
              className="stf-form-wrap"
            >
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <p className="stf-form-empty">No form block selected</p>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      <style>{`
        .stf-section {
          background: white;
          padding: 3rem 1.5rem;
        }
        @media (min-width: 768px) {
          .stf-section { padding: 5rem 2rem; }
        }
        .stf-container {
          max-width: 76rem;
          margin: 0 auto;
        }
        .stf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 900px) {
          .stf-grid {
            grid-template-columns: minmax(220px, 1fr) minmax(0, 2fr);
            gap: 4rem;
            align-items: start;
          }
        }

        /* ── Info column ── */
        .stf-info-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        @media (min-width: 900px) {
          .stf-info-col {
            align-items: flex-start;
            text-align: left;
          }
        }
        .stf-info-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          line-height: 1.2;
          margin: 0 0 1rem;
        }
        .stf-info-heading--spaced {
          margin-top: 2.25rem;
        }
        .stf-info-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text);
          text-decoration: none;
        }
        .stf-info-link {
          transition: color 0.2s ease;
        }
        .stf-info-link:hover,
        .stf-info-link:focus-visible {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* ── Hours list ── */
        .stf-hours {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }
        @media (min-width: 900px) {
          .stf-hours {
            align-items: flex-start;
          }
        }
        .stf-hours-row {
          display: flex;
          align-items: baseline;
          gap: 2rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 500;
          line-height: 1.5;
          color: var(--vibe-color-primary);
        }
        .stf-hours-day {
          min-width: 6rem;
          color: var(--vibe-color-primary);
        }
        .stf-hours-time {
          color: var(--vibe-color-primary);
        }

        /* ── Social row ── */
        .stf-socials {
          margin-top: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
        }
        @media (min-width: 900px) {
          .stf-socials {
            justify-content: flex-start;
          }
        }
        .stf-socials--empty {
          /* keep the row present (both wrappers must always render for the editor)
             but out of the way when neither URL is set */
          margin-top: 1rem;
          opacity: 0.35;
        }
        .stf-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--vibe-color-primary);
          font-size: 1.5rem;
          line-height: 1;
          transition: opacity 0.2s ease, transform 0.2s ease;
          text-decoration: none;
        }
        .stf-social-link:hover,
        .stf-social-link:focus-visible {
          opacity: 0.75;
          transform: translateY(-1px);
        }
        .stf-social-link--placeholder {
          cursor: default;
        }

        /* ── Form column ── */
        .stf-title {
          font-family: var(--vibe-font-heading);
          font-size: 2.25rem;
          font-weight: 800;
          color: var(--vibe-color-primary);
          line-height: 1.1;
          letter-spacing: -0.01em;
          margin: 0 0 2rem;
          white-space: nowrap;
          text-align: center;
        }
        @media (min-width: 768px) {
          .stf-title { font-size: 3.5rem; text-align: left; }
        }
        .stf-form-wrap {
          width: 100%;
        }
        .stf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduletourform.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Schedule A Tour',
    },
    'scheduletourform.address_label': {
      label: 'Address Heading',
      type: 'text',
      defaultValue: 'Address',
    },
    'scheduletourform.hours_label': {
      label: 'Hours Heading',
      type: 'text',
      defaultValue: 'Hours',
    },
  },
  slotGroups: {
    'scheduletourform.address': addressLinkDef,
    'scheduletourform.facebook_link': facebookLinkDef,
    'scheduletourform.instagram_link': instagramLinkDef,
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
